import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { updateQuestion } from '../../actions/';
import api from '../../../../../api/api';
import { getCachedLookup } from '../../../../../api/questionsLookupsService';
import AIChatModal from '../AIChatModal';
import BasicSettings from './BasicSettings';
import TextTypeSelector from './TextTypeSelector';
import ElementsSelector from './ElementsSelector';
import SkillsTreeSelector from './SkillsTreeSelector';
import './Info.css';

const Info = (props) => {
    const {
        currentStep,
        isForGame = false,
        QuestionId = 0,
        title = '',
        mark = 0,
        Level = 1,
        Position = false,
        IsRelativeToTopic = null,
        TopicIds = [],
        SkillIds = [],
        TextTypeId = null,
        ElementIds = [],
        typeId,
        type,
        setLevel,
        setPosition,
        setIsRelativeToTopic,
        setTopicIds,
        setSkillIds,
        setTextTypeId,
        setElementIds,
        setMark,
        submitMainInfo,
        onOpenAiMode,
        toast
    } = props;

    const { t } = useTranslation('common');
    const dispatch = useDispatch();
    const reduxQuestion = useSelector((state) => state.questions);

    const editorRef = useRef(null);

    // Lookups state
    const [topicsCategories, setTopicsCategories] = useState([]);
    const [textTypesList, setTextTypesList] = useState([]);
    const [elementsCategoriesList, setElementsCategoriesList] = useState([]);
    const [dimensionComponentsList, setDimensionComponentsList] = useState([]);
    const [elementsList, setElementsList] = useState([]);

    // Selection state
    const [selectedTextTypeId, setSelectedTextTypeId] = useState(TextTypeId);
    const [selectedElementIds, setSelectedElementIds] = useState(ElementIds || []);
    const [errorList, setErrorList] = useState([]);
    const [showAiChatModal, setShowAiChatModal] = useState(false);

    // Sync state when props change
    useEffect(() => {
        if (TextTypeId !== undefined) {
            setSelectedTextTypeId(TextTypeId);
        }
    }, [TextTypeId]);

    useEffect(() => {
        if (Array.isArray(ElementIds)) {
            setSelectedElementIds(ElementIds);
        }
    }, [ElementIds]);

    // Fetch lookups on mount
    useEffect(() => {
        // 1. Skills / Topics categories
        getCachedLookup('/QuestionsLookups/SkillsCategories')
            .then((res) => {
                let data = [];
                if (Array.isArray(res.data?.data)) {
                    data = res.data.data;
                } else if (Array.isArray(res.data?.data?.data)) {
                    data = res.data.data.data;
                } else if (Array.isArray(res.data)) {
                    data = res.data;
                }
                setTopicsCategories(data);
            })
            .catch((err) => console.error('Error fetching topics:', err));

        // 2. Text Types List
        getCachedLookup('/QuestionsLookups/TextTypesList')
            .then((res) => setTextTypesList(res.data?.data || res.data || []))
            .catch(() => {
                getCachedLookup('/QuestionsLookups/CategoriesList?groupBy=TextTypes')
                    .then((res) => setTextTypesList(res.data?.data || []))
                    .catch((err) => console.error('Error fetching text types:', err));
            });

        // 3. Elements Categories
        getCachedLookup('/QuestionsLookups/ElementsCategories')
            .then((res) => setElementsCategoriesList(res.data?.data || res.data || []))
            .catch(() => {
                getCachedLookup('/QuestionsLookups/CategoriesList?groupBy=Elements')
                    .then((res) => {
                        const raw = res.data?.data || [];
                        setElementsCategoriesList([
                            {
                                CategoryId: 1,
                                CategoryName: 'العناصر العامة',
                                Elements: raw.map((e) => ({
                                    ElementId: e.CategoryId,
                                    ElementName: e.CategoryName,
                                    CategoryId: 1
                                }))
                            }
                        ]);
                    })
                    .catch((err) => console.error('Error fetching elements:', err));
            });

        // 4. Dimension Components
        getCachedLookup('/QuestionsLookups/CategoriesList?groupBy=DimensionComponents')
            .then((res) => setDimensionComponentsList(res.data?.data || []))
            .catch((err) => console.error('Error fetching dimension components:', err));

        // 5. Raw Elements List for AI Modal
        getCachedLookup('/QuestionsLookups/CategoriesList?groupBy=Elements')
            .then((res) => setElementsList(res.data?.data || []))
            .catch((err) => console.error('Error fetching elements list:', err));
    }, []);

    // Fetch existing question skills/elements/textType
    const fetchQuestionSkills = useCallback((qId) => {
        if (!qId || qId <= 0) return;
        api.get(`/QuestionsLookups/Questions/${qId}/Skills`)
            .then((res) => {
                const data = res.data;
                if (data) {
                    const skills = data.skillIds || (Array.isArray(data.data) ? data.data : []);
                    if (Array.isArray(skills) && skills.length > 0) {
                        if (typeof setTopicIds === 'function') setTopicIds(skills);
                        if (typeof setSkillIds === 'function') setSkillIds(skills);
                    }
                    if (data.textTypeId !== undefined && data.textTypeId !== null) {
                        setSelectedTextTypeId(data.textTypeId);
                        if (typeof setTextTypeId === 'function') setTextTypeId(data.textTypeId);
                    }
                    if (Array.isArray(data.elementIds) && data.elementIds.length > 0) {
                        setSelectedElementIds(data.elementIds);
                        if (typeof setElementIds === 'function') setElementIds(data.elementIds);
                    }
                }
            })
            .catch((err) => console.error('Error fetching question skill details:', err));
    }, [setTopicIds, setSkillIds, setTextTypeId, setElementIds]);

    useEffect(() => {
        const targetQId = QuestionId || reduxQuestion?.id || reduxQuestion?.question?.Id;
        if (targetQId && targetQId > 0) {
            fetchQuestionSkills(targetQId);
        }
    }, [QuestionId, reduxQuestion?.id, reduxQuestion?.question?.Id, fetchQuestionSkills]);

    // Redux Question copy helper
    const updateReduxQuestion = useCallback((updates) => {
        if (reduxQuestion && reduxQuestion.question) {
            const copy = {
                ...reduxQuestion,
                question: {
                    ...reduxQuestion.question,
                    ...updates
                }
            };
            dispatch(updateQuestion(copy));
        }
    }, [reduxQuestion, dispatch]);

    // Handlers for Step 1
    const handleMarkChange = useCallback((val) => {
        if (typeof setMark === 'function') setMark(val);
        updateReduxQuestion({ Mark: val });
    }, [setMark, updateReduxQuestion]);

    const handleLevelChange = useCallback((lvl, name) => {
        if (typeof setLevel === 'function') setLevel(lvl);
        updateReduxQuestion({
            Level: {
                Id: lvl,
                Name: name
            }
        });
    }, [setLevel, updateReduxQuestion]);

    const handlePositionChange = useCallback((pos) => {
        if (typeof setPosition === 'function') setPosition(pos);
        updateReduxQuestion({ Position: pos });
    }, [setPosition, updateReduxQuestion]);

    const handleRelativityChange = useCallback((rel) => {
        if (typeof setIsRelativeToTopic === 'function') setIsRelativeToTopic(rel);
        updateReduxQuestion({ IsRelativeToTopic: rel });
    }, [setIsRelativeToTopic, updateReduxQuestion]);

    // Handlers for Step 2
    const handleSelectTextType = useCallback((tId) => {
        setSelectedTextTypeId(tId);
        if (typeof setTextTypeId === 'function') setTextTypeId(tId);
        updateReduxQuestion({ TextTypeId: tId });
    }, [setTextTypeId, updateReduxQuestion]);

    const handleToggleElement = useCallback((elementId) => {
        let currentIds = [...(selectedElementIds || [])];
        const numId = Number(elementId);
        const exists = currentIds.some((id) => Number(id) === numId);

        if (exists) {
            currentIds = currentIds.filter((id) => Number(id) !== numId);
        } else {
            currentIds.push(numId);
        }

        setSelectedElementIds(currentIds);
        if (typeof setElementIds === 'function') setElementIds(currentIds);
        updateReduxQuestion({ ElementIds: currentIds });
    }, [selectedElementIds, setElementIds, updateReduxQuestion]);

    const handleToggleTopic = useCallback((topicId) => {
        let currentTopicIds = [...(SkillIds.length > 0 ? SkillIds : (TopicIds || []))];
        if (currentTopicIds.includes(topicId)) {
            currentTopicIds = currentTopicIds.filter((id) => id !== topicId);
        } else {
            currentTopicIds.push(topicId);
        }

        if (typeof setSkillIds === 'function') setSkillIds(currentTopicIds);
        if (typeof setTopicIds === 'function') setTopicIds(currentTopicIds);

        updateReduxQuestion({
            Skills: currentTopicIds,
            Topics: currentTopicIds
        });
    }, [SkillIds, TopicIds, setSkillIds, setTopicIds, updateReduxQuestion]);

    // AI Chat integration
    const handleInsertAiHtml = useCallback((html, append = false) => {
        let finalHtml = html;
        if (append && window.getRedactor) {
            const current = window.getRedactor('#QuestionTitle') || '';
            finalHtml = current ? `${current}<br/>${html}` : html;
        }

        if (window.setRedactor) {
            window.setRedactor('#QuestionTitle', finalHtml);
        } else if (window.createRedactor) {
            window.createRedactor('#QuestionTitle', finalHtml);
        } else if (editorRef.current) {
            editorRef.current.value = finalHtml;
        }
    }, []);

    // Form Submission & Validation
    const validate = useCallback((questionMark = '', questionTitle = '') => {
        const errors = [];
        if (typeId !== 11 && (!questionMark || questionMark <= 0)) {
            errors.push({
                placeholder: t('Pages.Lessons.Questions.forms.QuestionMark.placeholder') || 'العلامة مطلوبة',
                name: 'Mark'
            });
        }
        if (!questionTitle || questionTitle.trim().length === 0) {
            errors.push({
                placeholder: t('Pages.Lessons.Questions.forms.QuestionTitle.placeholder') || 'نص السؤال مطلوب',
                name: 'QuestionTitle'
            });
        }
        return errors;
    }, [typeId, t]);

    const handleSubmit = useCallback(() => {
        const questionTitle = (window.getRedactor ? window.getRedactor('#QuestionTitle') : '') || editorRef.current?.value || '';
        const errors = validate(mark, questionTitle);

        if (errors.length > 0) {
            const errorItems = errors.map((err, idx) => (
                <li key={`error-${idx}`}>{err.placeholder}.</li>
            ));
            setErrorList(errorItems);

            if (toast) {
                toast.error(t('Error.AllInformationIsRequired.Body') || 'يرجى إكمال جميع الحقول المطلوبة', {
                    position: 'bottom-left',
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: false,
                    pauseOnHover: true,
                    draggable: true
                });
            }
        } else {
            setErrorList([]);
            if (typeof submitMainInfo === 'function') {
                submitMainInfo(
                    questionTitle,
                    mark,
                    Level || 1,
                    Position,
                    IsRelativeToTopic,
                    TopicIds,
                    null,
                    selectedTextTypeId,
                    selectedElementIds
                );
            }
        }
    }, [validate, mark, Level, Position, IsRelativeToTopic, TopicIds, selectedTextTypeId, selectedElementIds, submitMainInfo, toast, t]);

    const effectiveTopicIds = useMemo(() => {
        return SkillIds.length > 0 ? SkillIds : (TopicIds || []);
    }, [SkillIds, TopicIds]);

    return (
        <div className="modern-info-container">
            {/* Validation Error Banner */}
            <div className={`modern-error-alert ${errorList.length === 0 ? 'd-none' : ''}`} role="alert">
                <div className="modern-error-title">
                    <i className="fal fa-exclamation-circle ml-2"></i>
                    {t('Error.AllInformationIsRequired.Title') || 'الرجاء ملء كافة الحقول المطلوبة'}
                </div>
                <ul className="modern-error-list">{errorList}</ul>
            </div>

            {/* Step 1: Basic Settings & Details */}
            {(!currentStep || currentStep === 1) && (
                <BasicSettings
                    title={title}
                    QuestionId={QuestionId}
                    mark={mark}
                    level={Level}
                    position={Position}
                    isRelativeToTopic={IsRelativeToTopic}
                    isForGame={isForGame}
                    typeId={typeId}
                    type={type}
                    onOpenAiMode={onOpenAiMode}
                    onOpenLocalAiModal={() => setShowAiChatModal(true)}
                    editorRef={editorRef}
                    onMarkChange={handleMarkChange}
                    onLevelChange={handleLevelChange}
                    onPositionChange={handlePositionChange}
                    onRelativityChange={handleRelativityChange}
                />
            )}

            {/* Step 2: Educational Skills, Text Types & Elements Selection */}
            {(!currentStep || currentStep === 2) && (
                <div className="step-2-wrapper">
                    {/* Row with Section 1: Text Type & Section 2: Elements (Same Level) */}
                    {typeId === 11 && (
                        <div className="row mb-3">
                            <div className="col-12 col-lg-6 mb-3 mb-lg-0">
                                <TextTypeSelector
                                    textTypesList={textTypesList}
                                    selectedTextTypeId={selectedTextTypeId}
                                    onSelectTextType={handleSelectTextType}
                                />
                            </div>
                            <div className="col-12 col-lg-6">
                                <ElementsSelector
                                    elementsCategoriesList={elementsCategoriesList}
                                    selectedElementIds={selectedElementIds}
                                    onToggleElement={handleToggleElement}
                                />
                            </div>
                        </div>
                    )}

                    {/* Section 3: Educational Skills Tree (Full Width 100%) */}
                    <div className="w-100">
                        <SkillsTreeSelector
                            topicsCategories={topicsCategories}
                            selectedTopicIds={effectiveTopicIds}
                            onToggleTopic={handleToggleTopic}
                        />
                    </div>
                </div>
            )}

            {/* Save Button */}
            <div className="form-group w-100 text-center mt-4">
                <button
                    className="premium-btn-save"
                    title={t('Pages.Lessons.Questions.forms.tools.saveAndContinue') || 'حفظ ومتابعة'}
                    type="button"
                    onClick={handleSubmit}
                >
                    <i className="icon far fa-save"></i>
                    <span>
                        {isForGame
                            ? (t('Pages.Games.forms.LessonInfo.saveAndContinue') || 'حفظ ومتابعة')
                            : (t('Pages.Lessons.Questions.forms.tools.saveAndContinue') || 'حفظ ومتابعة')}
                    </span>
                </button>
            </div>

            {/* AI Text Formulation Chat Modal */}
            <AIChatModal
                show={showAiChatModal}
                onHide={() => setShowAiChatModal(false)}
                onInsertHtml={handleInsertAiHtml}
                topicsCategories={topicsCategories}
                textTypesList={textTypesList}
                dimensionComponentsList={dimensionComponentsList}
                elementsList={elementsList}
                TopicIds={effectiveTopicIds}
            />
        </div>
    );
};

export default React.memo(Info);
